import { A2uiOperation } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import { PersonFilterInput } from '~/types/api.graphql';
import { PULSE_TOPIC_ID_SEPARATOR } from '~/utils/pulse';

import { SurfaceDataModel } from '../A2uiSurface';
import { sanitizePulseFilters } from './pulseFilters';
import { PulseSelection } from './usePulsePeopleCount';

// A selectable Pulse topic is a full taxonomy path: category, sub-category and topic.
const PULSE_TOPIC_ID_SEGMENTS = 3;

export interface PulseFormBindings {
  /** Data-model path of the topics multi-select; null when the surface has none (the topics aren't edited). */
  topicsPath: string | null;
  /** Data-model paths of the VIQGeoFilter fields; each holds a JSON string of PersonFilterInput[]. */
  geoFilterPaths: string[];
}

/**
 * Finds where an agent-composed Pulse form keeps its topic selection and geo filters. The agent
 * picks the data-model paths, so they are read off the components: the topics are the multi-select
 * ChoicePicker whose options are all full Pulse topic ids (what the create/edit-Pulse skills
 * render), the geo filters are the VIQGeoFilter fields. Returns null for any other form.
 */
export const findPulseFormBindings = (operations: A2uiOperation[]): PulseFormBindings | null => {
  const components = operations.flatMap(readSurfaceComponents);
  const topicsPath = components.filter(isPulseTopicsPicker).map(readBoundPath).find(Boolean);
  if (!topicsPath) return null;
  return { topicsPath, geoFilterPaths: readGeoFilterPaths(components) };
};

/** Data-model paths of a surface's VIQGeoFilter fields. */
export const findGeoFilterPaths = (operations: A2uiOperation[]): string[] =>
  readGeoFilterPaths(operations.flatMap(readSurfaceComponents));

/** The form's current topic selection and geo filters, read from its live data model. */
export const readPulseFormSelection = (bindings: PulseFormBindings, dataModel: SurfaceDataModel): PulseSelection => {
  const topics = bindings.topicsPath ? readPointer(dataModel, bindings.topicsPath) : undefined;
  return {
    topicIds: Array.isArray(topics) ? topics.filter((topicId): topicId is string => typeof topicId === 'string') : [],
    filters: bindings.geoFilterPaths.flatMap((path) => parsePersonFilters(readPointer(dataModel, path))),
  };
};

const readGeoFilterPaths = (components: Record<string, unknown>[]): string[] =>
  components
    .filter((component) => component.component === 'VIQGeoFilter')
    .map(readBoundPath)
    .filter((path): path is string => !!path);

const isPulseTopicsPicker = (component: Record<string, unknown>): boolean => {
  if (component.component !== 'ChoicePicker' || component.variant !== 'multipleSelection') return false;
  const options = Array.isArray(component.options) ? component.options : [];
  return (
    options.length > 0 &&
    options.every((option) => {
      const value = asRecord(option)?.value;
      return typeof value === 'string' && isPulseTopicId(value);
    })
  );
};

const isPulseTopicId = (value: string): boolean => {
  const segments = value.split(PULSE_TOPIC_ID_SEPARATOR);
  return segments.length === PULSE_TOPIC_ID_SEGMENTS && segments.every((segment) => /^\S+$/.test(segment));
};

const readBoundPath = (component: Record<string, unknown>): string | null => {
  const path = asRecord(component.value)?.path;
  return typeof path === 'string' ? path : null;
};

const readSurfaceComponents = (operation: A2uiOperation): Record<string, unknown>[] => {
  const components = asRecord(operation.updateComponents)?.components;
  return Array.isArray(components) ? components.filter(isRecord) : [];
};

// Resolves a JSON pointer (`/form/topics`) against the data model.
const readPointer = (dataModel: SurfaceDataModel, path: string): unknown =>
  path
    .split('/')
    .filter(Boolean)
    .reduce<unknown>((node, segment) => (isRecord(node) || Array.isArray(node) ? node[segment] : undefined), dataModel);

// A VIQGeoFilter value is a JSON string of PersonFilterInput[]; empty until the user confirms the modal.
const parsePersonFilters = (value: unknown): PersonFilterInput[] => {
  if (typeof value !== 'string' || !value) return [];
  try {
    return sanitizePulseFilters(JSON.parse(value));
  } catch {
    return [];
  }
};

const asRecord = (value: unknown): Record<string, unknown> | null => (isRecord(value) ? value : null);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
